/**
 * SchoolReportPage — Access-controlled school/camp report page.
 * Route: /school/:slug
 * Access controlled by access code (URL param or sessionStorage).
 */

import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { db } from '@/lib/firebase';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  query,
  where,
  limit,
  serverTimestamp,
  Timestamp,
} from 'firebase/firestore';
import type { ParsedRider } from '@/pages/admin/WishIntelligence';

// ─── Types ────────────────────────────────────────────────────────────────────

interface Org {
  id: string;
  name: string;
  type: 'school' | 'camp';
  slug: string;
  season: string;
  description: string;
  contactEmail: string;
  accessCode: string;
}

interface SchoolInquiry {
  studentFirstName: string;
  studentLastName: string;
  grade: string;
  guardianName: string;
  guardianEmail: string;
  guardianPhone: string;
  pickupAddress: string;
  pickupZipcode: string;
  daysOrWeeks: string;
  notes: string;
}

interface CampRoster {
  type: 'camp';
  bookedRiders: ParsedRider[];
  interestedRiders: ParsedRider[];
}

interface SchoolRoster {
  type: 'school';
  inquiries: SchoolInquiry[];
}

type Roster = CampRoster | SchoolRoster | null;

// ─── Helpers ─────────────────────────────────────────────────────────────────

const WEEKS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];

function studentsOnLoopWeek(loop: 'early' | 'late', week: number, riders: ParsedRider[]) {
  return riders
    .filter(r => r.loop === loop && r.weeks.includes(week))
    .reduce((sum, r) => sum + r.students, 0);
}

// ─── Interest form ────────────────────────────────────────────────────────────

interface CampInterestForm {
  name: string;
  email: string;
  phone: string;
  pickup: string;
  students: string;
  weeks: string;
  preferredTime: string;
  notes: string;
}

const BLANK_CAMP_FORM: CampInterestForm = {
  name: '', email: '', phone: '', pickup: '',
  students: '1', weeks: '', preferredTime: '', notes: '',
};

// ─── Access Gate ──────────────────────────────────────────────────────────────

function AccessGate({
  orgName,
  description,
  onSubmit,
  error,
}: {
  orgName: string;
  description: string;
  onSubmit: (code: string) => void;
  error: string | null;
}) {
  const [code, setCode] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(code.trim().toUpperCase());
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-2xl border border-gray-200 shadow-lg p-8 text-center">
          {/* Logo placeholder */}
          <div className="w-16 h-16 rounded-full bg-shuttlebee-yellow flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-shuttlebee-dark" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
            </svg>
          </div>
          <h1 className="text-xl font-bold text-gray-900 mb-1">{orgName}</h1>
          {description && (
            <p className="text-sm text-gray-500 mb-6">{description}</p>
          )}
          <p className="text-sm text-gray-600 mb-4">Enter your access code to view the report.</p>
          <form onSubmit={handleSubmit} className="flex flex-col gap-3">
            <input
              type="text"
              value={code}
              onChange={e => setCode(e.target.value.toUpperCase())}
              placeholder="Access Code"
              maxLength={8}
              className="w-full rounded-xl border border-gray-300 px-4 py-3 text-center text-lg font-mono tracking-widest focus:outline-none focus:ring-2 focus:ring-primary"
              autoFocus
            />
            {error && (
              <p className="text-xs text-red-600 font-medium">{error}</p>
            )}
            <button
              type="submit"
              className="w-full bg-shuttlebee-yellow hover:bg-shuttlebee-gold text-shuttlebee-dark font-semibold rounded-xl py-3 transition-colors"
            >
              View Report
            </button>
          </form>
          <p className="text-xs text-gray-400 mt-4">Powered by Shuttlebee</p>
        </div>
      </div>
    </div>
  );
}

// ─── Camp Report ──────────────────────────────────────────────────────────────

function CampReport({ org, roster }: { org: Org; roster: CampRoster }) {
  const [form, setForm] = useState<CampInterestForm>(BLANK_CAMP_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const { bookedRiders } = roster;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name || !form.email) return;
    try {
      setSubmitting(true);
      await addDoc(collection(db, 'campInterest'), {
        ...form,
        orgId: org.id,
        slug: org.slug,
        orgName: org.name,
        season: org.season,
        submittedAt: serverTimestamp(),
      });
      setSubmitted(true);
    } catch (err) {
      console.error('Failed to submit interest:', err);
      setSubmitError('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero */}
      <div className="bg-gradient-to-br from-shuttlebee-yellow to-shuttlebee-gold py-12 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-3xl font-bold text-shuttlebee-dark mb-2">{org.name}</h1>
          {org.season && (
            <p className="text-lg text-shuttlebee-dark/70 font-medium">{org.season}</p>
          )}
          {org.description && (
            <p className="text-sm text-shuttlebee-dark/60 mt-2">{org.description}</p>
          )}
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-8 flex flex-col gap-8">
        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {[
            { label: 'Booked Families', value: bookedRiders.length, color: 'text-blue-600' },
            { label: 'Booked Students', value: bookedRiders.reduce((s, r) => s + r.students, 0), color: 'text-blue-600' },
            { label: 'Interested Families', value: roster.interestedRiders.length, color: 'text-green-600' },
            { label: 'Interested Students', value: roster.interestedRiders.reduce((s, r) => s + r.students, 0), color: 'text-green-600' },
          ].map(stat => (
            <div key={stat.label} className="bg-white rounded-xl border border-gray-200 p-4 text-center shadow-sm">
              <div className={`text-2xl font-bold ${stat.color}`}>{stat.value}</div>
              <div className="text-xs text-gray-500 mt-0.5">{stat.label}</div>
            </div>
          ))}
        </div>

        {/* Weekly enrollment grid */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-100">
            <h2 className="font-semibold text-gray-900">Weekly Enrollment</h2>
            <p className="text-xs text-gray-500 mt-0.5">Number of students per loop per week (booked)</p>
          </div>
          <div className="overflow-x-auto px-5 py-4">
            <table className="w-full text-sm text-center">
              <thead>
                <tr className="border-b border-gray-100">
                  <th className="text-left py-2 font-medium text-gray-700">Loop</th>
                  {WEEKS.map(w => (
                    <th key={w} className="py-2 font-medium text-gray-500 min-w-[40px]">W{w}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {(['early', 'late'] as const).map(loop => (
                  <tr key={loop} className="border-b border-gray-50">
                    <td className="text-left py-2 font-medium text-gray-700 capitalize pr-3">{loop}</td>
                    {WEEKS.map(w => {
                      const count = studentsOnLoopWeek(loop, w, bookedRiders);
                      return (
                        <td key={w} className={`py-2 font-semibold ${count > 0 ? 'text-green-700' : 'text-gray-300'}`}>
                          {count > 0 ? count : '—'}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Interest form */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-100">
            <h2 className="font-semibold text-gray-900">Express Interest</h2>
            <p className="text-xs text-gray-500 mt-0.5">Let us know you're interested and we'll be in touch.</p>
          </div>
          <div className="px-5 py-4">
            {submitted ? (
              <div className="text-center py-6">
                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <svg className="w-6 h-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <p className="font-semibold text-gray-900">Thanks, we got it!</p>
                <p className="text-sm text-gray-500 mt-1">We'll be in touch soon.</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                  <input
                    type="text"
                    required
                    value={form.name}
                    onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                  <input
                    type="email"
                    required
                    value={form.email}
                    onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                  <input
                    type="tel"
                    value={form.phone}
                    onChange={e => setForm(f => ({ ...f, phone: e.target.value }))}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Pickup Location</label>
                  <input
                    type="text"
                    value={form.pickup}
                    onChange={e => setForm(f => ({ ...f, pickup: e.target.value }))}
                    placeholder="Neighborhood or address"
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Students</label>
                  <input
                    type="number"
                    min="1"
                    max="10"
                    value={form.students}
                    onChange={e => setForm(f => ({ ...f, students: e.target.value }))}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Interested Weeks</label>
                  <input
                    type="text"
                    value={form.weeks}
                    onChange={e => setForm(f => ({ ...f, weeks: e.target.value }))}
                    placeholder="e.g. 1, 2, 3"
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Preferred Pickup Time</label>
                  <input
                    type="text"
                    value={form.preferredTime}
                    onChange={e => setForm(f => ({ ...f, preferredTime: e.target.value }))}
                    placeholder="e.g. Early Loop ~7:30 AM"
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                  <input
                    type="text"
                    value={form.notes}
                    onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
                {submitError && (
                  <div className="sm:col-span-2 text-xs text-red-600">{submitError}</div>
                )}
                <div className="sm:col-span-2">
                  <button
                    type="submit"
                    disabled={submitting}
                    className="bg-shuttlebee-yellow hover:bg-shuttlebee-gold text-shuttlebee-dark font-semibold rounded-xl px-6 py-2.5 text-sm transition-colors disabled:opacity-50"
                  >
                    {submitting ? 'Submitting…' : 'Submit Interest'}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>

        <Footer />
      </div>
    </div>
  );
}

// ─── School Report ────────────────────────────────────────────────────────────

function SchoolReport({ org, inquiries }: { org: Org; inquiries: SchoolInquiry[] }) {
  const uniqueZips = new Set(inquiries.map(r => r.pickupZipcode).filter(Boolean)).size;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-br from-shuttlebee-yellow to-shuttlebee-gold py-12 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-3xl font-bold text-shuttlebee-dark mb-2">{org.name}</h1>
          {org.season && (
            <p className="text-lg text-shuttlebee-dark/70 font-medium">{org.season}</p>
          )}
          {org.description && (
            <p className="text-sm text-shuttlebee-dark/60 mt-2">{org.description}</p>
          )}
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-8 flex flex-col gap-6">
        {/* Stats */}
        <div className="grid grid-cols-2 gap-4">
          <div className="bg-white rounded-xl border border-gray-200 p-4 text-center shadow-sm">
            <div className="text-3xl font-bold text-blue-600">{inquiries.length}</div>
            <div className="text-xs text-gray-500 mt-0.5">Total Inquiries</div>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-4 text-center shadow-sm">
            <div className="text-3xl font-bold text-gray-800">{uniqueZips}</div>
            <div className="text-xs text-gray-500 mt-0.5">Unique Pickup Areas</div>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-100">
            <h2 className="font-semibold text-gray-900">Student Inquiries</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th className="text-left px-4 py-3 font-semibold text-gray-700">Student Name</th>
                  <th className="text-left px-4 py-3 font-semibold text-gray-700">Grade</th>
                  <th className="text-left px-4 py-3 font-semibold text-gray-700">Guardian</th>
                  <th className="text-left px-4 py-3 font-semibold text-gray-700">Email</th>
                  <th className="text-left px-4 py-3 font-semibold text-gray-700">Pickup Address</th>
                  <th className="text-left px-4 py-3 font-semibold text-gray-700">Days / Weeks</th>
                  <th className="text-left px-4 py-3 font-semibold text-gray-700">Notes</th>
                </tr>
              </thead>
              <tbody>
                {inquiries.map((r, i) => (
                  <tr key={i} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="px-4 py-3 text-gray-900">
                      {r.studentFirstName} {r.studentLastName}
                    </td>
                    <td className="px-4 py-3 text-gray-600">{r.grade || '—'}</td>
                    <td className="px-4 py-3 text-gray-600">{r.guardianName || '—'}</td>
                    <td className="px-4 py-3 text-gray-600">{r.guardianEmail}</td>
                    <td className="px-4 py-3 text-gray-600">
                      {r.pickupAddress && <span>{r.pickupAddress}</span>}
                      {r.pickupZipcode && <span className="text-gray-400 ml-1">{r.pickupZipcode}</span>}
                      {!r.pickupAddress && !r.pickupZipcode && '—'}
                    </td>
                    <td className="px-4 py-3 text-gray-600">{r.daysOrWeeks || '—'}</td>
                    <td className="px-4 py-3 text-gray-500 text-xs">{r.notes || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <Footer />
      </div>
    </div>
  );
}

// ─── Footer ───────────────────────────────────────────────────────────────────

function Footer() {
  return (
    <div className="text-center py-4">
      <p className="text-xs text-gray-400">Powered by Shuttlebee</p>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function SchoolReportPage() {
  const { slug } = useParams<{ slug: string }>();
  const [searchParams] = useSearchParams();
  const codeParam = searchParams.get('code')?.toUpperCase() ?? '';

  const [status, setStatus] = useState<'loading' | 'not_found' | 'gate' | 'report'>('loading');
  const [org, setOrg] = useState<Org | null>(null);
  const [roster, setRoster] = useState<Roster>(null);
  const [codeError, setCodeError] = useState<string | null>(null);

  useEffect(() => {
    if (!slug) {
      setStatus('not_found');
      return;
    }

    const loadOrg = async () => {
      try {
        const q = query(
          collection(db, 'organizations'),
          where('slug', '==', slug),
          limit(1),
        );
        const snap = await getDocs(q);
        if (snap.empty) {
          setStatus('not_found');
          return;
        }
        const docData = snap.docs[0];
        const data = docData.data();
        const loadedOrg: Org = {
          id: docData.id,
          name: data.name ?? '',
          type: data.type ?? 'school',
          slug: data.slug ?? '',
          season: data.season ?? '',
          description: data.description ?? '',
          contactEmail: data.contactEmail ?? '',
          accessCode: data.accessCode ?? '',
        };
        setOrg(loadedOrg);

        // Check access
        const sessionKey = `access_${slug}`;
        const storedCode = sessionStorage.getItem(sessionKey)?.toUpperCase() ?? '';
        const isAuthorized =
          codeParam === loadedOrg.accessCode || storedCode === loadedOrg.accessCode;

        if (isAuthorized) {
          sessionStorage.setItem(sessionKey, loadedOrg.accessCode);
          await loadRoster(docData.id);
          setStatus('report');
        } else {
          setStatus('gate');
        }
      } catch (err) {
        console.error('Failed to load org:', err);
        setStatus('not_found');
      }
    };

    loadOrg();
  }, [slug, codeParam]);

  const loadRoster = async (orgId: string) => {
    try {
      const snap = await getDoc(doc(db, 'organizations', orgId, 'rosters', 'current'));
      if (!snap.exists()) {
        setRoster(null);
        return;
      }
      const data = snap.data();
      if (data.type === 'camp') {
        setRoster({
          type: 'camp',
          bookedRiders: (data.bookedRiders as ParsedRider[]) ?? [],
          interestedRiders: (data.interestedRiders as ParsedRider[]) ?? [],
        });
      } else {
        setRoster({
          type: 'school',
          inquiries: (data.inquiries as SchoolInquiry[]) ?? [],
        });
      }
    } catch (err) {
      console.error('Failed to load roster:', err);
      setRoster(null);
    }
  };

  const handleCodeSubmit = async (code: string) => {
    if (!org) return;
    if (code === org.accessCode) {
      sessionStorage.setItem(`access_${slug}`, org.accessCode);
      await loadRoster(org.id);
      setStatus('report');
      setCodeError(null);
    } else {
      setCodeError('Incorrect access code. Please try again.');
    }
  };

  if (status === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-shuttlebee-yellow border-t-transparent rounded-full animate-spin" />
          <p className="text-gray-600">Loading…</p>
        </div>
      </div>
    );
  }

  if (status === 'not_found') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Page not found</h1>
          <p className="text-gray-500 text-sm">This organization page doesn't exist.</p>
          <p className="text-xs text-gray-400 mt-4">Powered by Shuttlebee</p>
        </div>
      </div>
    );
  }

  if (status === 'gate' && org) {
    return (
      <AccessGate
        orgName={org.name}
        description={org.description}
        onSubmit={handleCodeSubmit}
        error={codeError}
      />
    );
  }

  if (status === 'report' && org) {
    if (!roster) {
      return (
        <div className="min-h-screen bg-gray-50">
          <div className="bg-gradient-to-br from-shuttlebee-yellow to-shuttlebee-gold py-12 px-4">
            <div className="max-w-4xl mx-auto text-center">
              <h1 className="text-3xl font-bold text-shuttlebee-dark">{org.name}</h1>
              {org.season && <p className="text-lg text-shuttlebee-dark/70 mt-1">{org.season}</p>}
            </div>
          </div>
          <div className="max-w-4xl mx-auto px-4 py-12 text-center">
            <p className="text-gray-500">No data has been imported for this organization yet.</p>
            <p className="text-xs text-gray-400 mt-8">Powered by Shuttlebee</p>
          </div>
        </div>
      );
    }

    if (roster.type === 'camp') {
      return <CampReport org={org} roster={roster} />;
    }

    return <SchoolReport org={org} inquiries={roster.inquiries} />;
  }

  return null;
}
